import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { initializeFirestore } from './firestore-init';

// Initialize Firestore (with emulator support)
const db = initializeFirestore();

export interface BroadcastMessage {
  id: string;
  broadcastCode: string;
  sourceText: string;
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
  timestamp: Date;
  speakerId: string;
  speakerEmail: string;
}

export interface BroadcastSession {
  code: string;
  speakerId: string;
  speakerEmail: string;
  sourceLanguage: string;
  targetLanguages: string[]; // Default + up to 3 extra
  createdAt: Date;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BroadcastTranslationService {
  private readonly BROADCAST_COLLECTION = 'broadcasts';
  private readonly MESSAGES_COLLECTION = 'broadcastMessages';

  private currentSession$ = new BehaviorSubject<BroadcastSession | null>(null);
  private messages$ = new BehaviorSubject<BroadcastMessage[]>([]);
  private isListening$ = new BehaviorSubject<boolean>(false);
  private unsubscribeListener?: () => void;

  constructor(private http: HttpClient, private authService: AuthService) {}

  get currentSession(): Observable<BroadcastSession | null> {
    return this.currentSession$.asObservable();
  }

  get messages(): Observable<BroadcastMessage[]> {
    return this.messages$.asObservable();
  }

  get isListening(): Observable<boolean> {
    return this.isListening$.asObservable();
  }

  /**
   * Generate a random 6-digit code for broadcast session
   */
  private generateBroadcastCode(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  /**
   * Create a new broadcast session (speaker side)
   */
  async createBroadcastSession(
    sourceLanguage: string,
    targetLanguages: string[] // Should include default + up to 3 extra
  ): Promise<string> {
    try {
      console.log(
        '[BroadcastTranslation] 🔄 Starting createBroadcastSession...'
      );
      const user = await this.authService.getCurrentUser();
      if (!user || !user.email) {
        console.error('[BroadcastTranslation] ❌ User not authenticated');
        throw new Error('User not authenticated');
      }

      console.log('[BroadcastTranslation] ✅ User authenticated:', user.email);
      const code = this.generateBroadcastCode();
      console.log('[BroadcastTranslation] 🎲 Generated code:', code);

      const session: BroadcastSession = {
        code,
        speakerId: user.userId || user.email,
        speakerEmail: user.email,
        sourceLanguage,
        targetLanguages,
        createdAt: new Date(),
        isActive: true,
      };

      console.log('[BroadcastTranslation] 📝 Saving session to Firestore...');
      console.log(
        '[BroadcastTranslation] 📍 Collection:',
        this.BROADCAST_COLLECTION
      );
      console.log('[BroadcastTranslation] 📍 Document ID:', code);

      // Save session to Firestore
      const sessionRef = doc(db, this.BROADCAST_COLLECTION, code);

      const sessionData = {
        code: session.code,
        speakerId: session.speakerId,
        speakerEmail: session.speakerEmail,
        sourceLanguage: session.sourceLanguage,
        targetLanguages: session.targetLanguages,
        isActive: session.isActive,
        createdAt: serverTimestamp(),
      };

      console.log('[BroadcastTranslation] 📦 Session data to save:', {
        ...sessionData,
        createdAt: '[serverTimestamp]',
      });

      try {
        console.log('[BroadcastTranslation] 🔄 Calling setDoc...');
        console.log('[BroadcastTranslation] 🔍 Firestore instance:', {
          type: typeof db,
          app: db?.app?.name,
          projectId: db?.app?.options?.projectId,
          databaseId: (db as any)?._databaseId?.databaseId,
          settings: (db as any)?._settings,
        });
        console.log('[BroadcastTranslation] 🔍 Environment:', {
          production: (window as any)?.__env?.production ?? 'unknown',
          apiUrl: (window as any)?.__env?.apiUrl ?? 'unknown',
        });

        // Add timeout to detect if setDoc hangs (longer timeout for production)
        const timeoutDuration = 15000; // 15 seconds for production Firestore
        const setDocPromise = setDoc(sessionRef, sessionData);
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () =>
              reject(
                new Error(
                  `setDoc timeout after ${timeoutDuration}ms - Firestore may not be connected`
                )
              ),
            timeoutDuration
          );
        });

        await Promise.race([setDocPromise, timeoutPromise]);

        console.log(
          '[BroadcastTranslation] ✅ Session saved to Firestore successfully'
        );
        console.log(
          '[BroadcastTranslation] 📍 Document path: broadcasts/' + code
        );

        // Verify the document was created by reading it back
        try {
          const verifyRef = doc(db, this.BROADCAST_COLLECTION, code);
          const verifySnap = await getDoc(verifyRef);
          if (verifySnap.exists()) {
            console.log(
              '[BroadcastTranslation] ✅ Verified: Document exists in Firestore'
            );
            console.log(
              '[BroadcastTranslation] 📄 Document data:',
              verifySnap.data()
            );
          } else {
            console.warn(
              '[BroadcastTranslation] ⚠️ Warning: Document not found after save'
            );
            console.warn(
              '[BroadcastTranslation] ⚠️ This might indicate a Firestore rules issue or project mismatch'
            );
          }
        } catch (verifyError: any) {
          console.warn(
            '[BroadcastTranslation] ⚠️ Could not verify document:',
            verifyError
          );
          console.warn('[BroadcastTranslation] ⚠️ Verify error details:', {
            message: verifyError?.message,
            code: verifyError?.code,
            name: verifyError?.name,
          });
        }
      } catch (firestoreError: any) {
        console.error(
          '[BroadcastTranslation] ❌ Firestore save failed:',
          firestoreError
        );
        console.error('[BroadcastTranslation] Error details:', {
          message: firestoreError?.message,
          code: firestoreError?.code,
          name: firestoreError?.name,
          stack: firestoreError?.stack,
          cause: firestoreError?.cause,
        });

        // Check for specific error types
        if (firestoreError?.code === 'permission-denied') {
          console.error(
            '[BroadcastTranslation] ❌ PERMISSION DENIED - Check Firestore security rules'
          );
          console.error('[BroadcastTranslation] 📋 Collection: broadcasts');
          console.error('[BroadcastTranslation] 📋 Document ID:', code);
          console.error(
            '[BroadcastTranslation] 💡 Make sure Firestore rules allow create on broadcasts collection'
          );
        } else if (
          firestoreError?.code === 'not-found' ||
          firestoreError?.message?.includes('not found')
        ) {
          console.error(
            '[BroadcastTranslation] ❌ PROJECT NOT FOUND - Check Firebase project configuration'
          );
          console.error(
            '[BroadcastTranslation] 💡 Verify projectId in environment file matches your Firebase project'
          );
        } else if (
          firestoreError?.message?.includes('timeout') ||
          firestoreError?.message?.includes('emulator')
        ) {
          console.error(
            '[BroadcastTranslation] ⚠️ Possible connection issue. Check:'
          );
          console.error(
            '[BroadcastTranslation] 1. Is Firestore emulator running? (firebase emulators:start)'
          );
          console.error(
            '[BroadcastTranslation] 2. Check browser console for [FirestoreInit] logs'
          );
          console.error(
            '[BroadcastTranslation] 3. Verify emulator UI at http://localhost:4000'
          );
        }

        // Don't continue - throw the error so user knows it failed
        throw new Error(
          `Failed to save broadcast session: ${
            firestoreError?.message || 'Unknown error'
          }`
        );
      }

      // Always set the session locally, even if Firestore failed
      this.currentSession$.next(session);
      console.log(
        `[BroadcastTranslation] ✅ Created broadcast session: ${code}`
      );
      console.log(`[BroadcastTranslation] 📋 Session languages:`, {
        sourceLanguage,
        targetLanguages,
        count: targetLanguages.length,
      });
      return code;
    } catch (error) {
      console.error('[BroadcastTranslation] ❌ Error creating session:', error);
      console.error('[BroadcastTranslation] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        error,
      });
      throw error;
    }
  }

  /**
   * Join a broadcast session (listener side)
   */
  async joinBroadcastSession(code: string): Promise<BroadcastSession> {
    try {
      console.log('[BroadcastTranslation] 🔄 Joining broadcast session:', code);
      const sessionRef = doc(db, this.BROADCAST_COLLECTION, code);

      console.log(
        '[BroadcastTranslation] 📡 Fetching session document from Firestore...'
      );
      const sessionSnap = await getDoc(sessionRef);
      console.log(
        '[BroadcastTranslation] ✅ Session document fetched successfully'
      );

      if (!sessionSnap.exists()) {
        throw new Error(
          'Broadcast session not found. Please check the code and try again.'
        );
      }

      const sessionData = sessionSnap.data() as any;
      const session: BroadcastSession = {
        code: sessionData['code'],
        speakerId: sessionData['speakerId'],
        speakerEmail: sessionData['speakerEmail'],
        sourceLanguage: sessionData['sourceLanguage'],
        targetLanguages: sessionData['targetLanguages'] || [],
        createdAt: sessionData['createdAt']?.toDate() || new Date(),
        isActive: sessionData['isActive'] !== false,
      };

      if (!session.isActive) {
        throw new Error('Broadcast session is no longer active');
      }

      this.currentSession$.next(session);
      console.log('[BroadcastTranslation] ✅ Joined broadcast session:', code);
      return session;
    } catch (error: any) {
      console.error('[BroadcastTranslation] ❌ Error joining session:', error);
      const errorMessage = error?.message || String(error);
      const errorCode = error?.code;

      // Provide user-friendly error messages based on error type
      if (
        errorMessage.includes('offline') ||
        errorMessage.includes('Failed to get document') ||
        errorCode === 'unavailable'
      ) {
        throw new Error(
          'Unable to connect to the server. Please check your internet connection and try again.'
        );
      }
      if (
        errorMessage.includes('not found') ||
        errorMessage.includes('does not exist')
      ) {
        throw new Error(
          'Broadcast session not found. Please check the code and try again.'
        );
      }
      if (
        errorMessage.includes('timeout') ||
        errorCode === 'deadline-exceeded'
      ) {
        throw new Error(
          'Connection timeout. Please check your internet connection and try again.'
        );
      }

      // Re-throw original error if it's already a user-friendly message
      if (error instanceof Error && error.message) {
        throw error;
      }
      throw new Error(
        errorMessage || 'Failed to join broadcast session. Please try again.'
      );
    }
  }

  /**
   * TESTING: Start listening to ALL languages (no filtering)
   * This will show all messages regardless of targetLanguage
   */
  startListeningAllLanguages(code: string): void {
    if (this.unsubscribeListener) {
      this.unsubscribeListener();
    }

    this.isListening$.next(true);
    const messagesRef = collection(db, this.MESSAGES_COLLECTION);

    // Query only by broadcastCode (no targetLanguage filter)
    const q = query(
      messagesRef,
      where('broadcastCode', '==', code),
      orderBy('timestamp', 'desc') // Latest first
    );

    // First, load all existing messages
    this.loadExistingMessagesAllLanguages(code).catch((error: any) => {
      console.error(
        '[BroadcastTranslation] Error loading existing messages (all languages):',
        error
      );
    });

    // Then listen for new messages
    this.unsubscribeListener = onSnapshot(
      q,
      (snapshot) => {
        const messages: BroadcastMessage[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as any;
          messages.push({
            id: docSnap.id,
            broadcastCode: data['broadcastCode'],
            sourceText: data['sourceText'],
            translatedText: data['translatedText'],
            sourceLanguage: data['sourceLanguage'],
            targetLanguage: data['targetLanguage'],
            timestamp: data['timestamp']?.toDate() || new Date(),
            speakerId: data['speakerId'],
            speakerEmail: data['speakerEmail'],
          });
        });

        // Sort by timestamp - latest first (descending order)
        messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        this.messages$.next(messages);
        console.log(
          `[BroadcastTranslation] ✅ TESTING: Received ${messages.length} messages (ALL languages)`
        );
        if (messages.length > 0) {
          const languages = [...new Set(messages.map((m) => m.targetLanguage))];
          console.log(
            `[BroadcastTranslation] Languages in messages:`,
            languages
          );
        }
      },
      (error: any) => {
        console.error(
          '[BroadcastTranslation] Error listening to messages (all languages):',
          error
        );
        // If orderBy fails, try without it
        if (error.code === 'failed-precondition') {
          console.warn(
            '[BroadcastTranslation] Index missing, trying without orderBy'
          );
          const qFallback = query(
            messagesRef,
            where('broadcastCode', '==', code)
          );
          this.unsubscribeListener = onSnapshot(
            qFallback,
            (snapshot) => {
              const messages: BroadcastMessage[] = [];
              snapshot.forEach((docSnap) => {
                const data = docSnap.data() as any;
                messages.push({
                  id: docSnap.id,
                  broadcastCode: data['broadcastCode'],
                  sourceText: data['sourceText'],
                  translatedText: data['translatedText'],
                  sourceLanguage: data['sourceLanguage'],
                  targetLanguage: data['targetLanguage'],
                  timestamp: data['timestamp']?.toDate() || new Date(),
                  speakerId: data['speakerId'],
                  speakerEmail: data['speakerEmail'],
                });
              });
              messages.sort(
                (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
              );
              this.messages$.next(messages);
              console.log(
                `[BroadcastTranslation] ✅ TESTING: Received ${messages.length} messages (ALL languages, fallback)`
              );
            },
            (err) => {
              console.error(
                '[BroadcastTranslation] Error in fallback listener:',
                err
              );
              this.isListening$.next(false);
            }
          );
        } else {
          this.isListening$.next(false);
        }
      }
    );
  }

  /**
   * Load all existing messages for a broadcast code (all languages - TESTING)
   */
  private async loadExistingMessagesAllLanguages(code: string): Promise<void> {
    try {
      const messagesRef = collection(db, this.MESSAGES_COLLECTION);
      const q = query(
        messagesRef,
        where('broadcastCode', '==', code),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      const messages: BroadcastMessage[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as any;
        messages.push({
          id: docSnap.id,
          broadcastCode: data['broadcastCode'],
          sourceText: data['sourceText'],
          translatedText: data['translatedText'],
          sourceLanguage: data['sourceLanguage'],
          targetLanguage: data['targetLanguage'],
          timestamp: data['timestamp']?.toDate() || new Date(),
          speakerId: data['speakerId'],
          speakerEmail: data['speakerEmail'],
        });
      });

      messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      this.messages$.next(messages);
      console.log(
        `[BroadcastTranslation] ✅ TESTING: Loaded ${messages.length} existing messages (ALL languages)`
      );
      const languages = [...new Set(messages.map((m) => m.targetLanguage))];
      console.log(`[BroadcastTranslation] Languages found:`, languages);
    } catch (error: any) {
      // If orderBy fails, try without it
      if (error.code === 'failed-precondition') {
        console.warn(
          '[BroadcastTranslation] Index missing, loading without orderBy'
        );
        const messagesRef = collection(db, this.MESSAGES_COLLECTION);
        const q = query(messagesRef, where('broadcastCode', '==', code));
        const snapshot = await getDocs(q);
        const messages: BroadcastMessage[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as any;
          messages.push({
            id: docSnap.id,
            broadcastCode: data['broadcastCode'],
            sourceText: data['sourceText'],
            translatedText: data['translatedText'],
            sourceLanguage: data['sourceLanguage'],
            targetLanguage: data['targetLanguage'],
            timestamp: data['timestamp']?.toDate() || new Date(),
            speakerId: data['speakerId'],
            speakerEmail: data['speakerEmail'],
          });
        });
        messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        this.messages$.next(messages);
        console.log(
          `[BroadcastTranslation] ✅ TESTING: Loaded ${messages.length} existing messages (ALL languages, fallback)`
        );
        const languages = [...new Set(messages.map((m) => m.targetLanguage))];
        console.log(`[BroadcastTranslation] Languages found:`, languages);
      } else {
        throw error;
      }
    }
  }

  /**
   * Start listening to broadcast messages (listener side)
   * This will load all existing messages and listen for new ones
   */
  startListening(code: string, targetLanguage: string): void {
    if (this.unsubscribeListener) {
      this.unsubscribeListener();
    }

    this.isListening$.next(true);
    const messagesRef = collection(db, this.MESSAGES_COLLECTION);

    // Create a query to filter by broadcastCode and targetLanguage
    // Note: Firestore requires composite index for multiple where clauses
    // For now, we'll query by broadcastCode and filter targetLanguage in code
    // In production, you should create a composite index in Firestore console
    const q = query(
      messagesRef,
      where('broadcastCode', '==', code),
      where('targetLanguage', '==', targetLanguage),
      orderBy('timestamp', 'desc') // Latest first
    );

    // First, try to load all existing messages
    this.loadExistingMessages(code, targetLanguage)
      .then(() => {
        console.log(
          '[BroadcastTranslation] Loaded existing messages, now listening for new ones'
        );
      })
      .catch(async (error: any) => {
        // If loading fails due to missing index, use fallback
        if (error.code === 'failed-precondition') {
          console.warn(
            '[BroadcastTranslation] Composite index missing for loadExistingMessages, using fallback'
          );
          await this.loadExistingMessagesFallback(code, targetLanguage);
        } else {
          console.error(
            '[BroadcastTranslation] Error loading existing messages:',
            error
          );
        }
      });

    // Then listen for new messages
    this.unsubscribeListener = onSnapshot(
      q,
      (snapshot) => {
        const messages: BroadcastMessage[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as any;
          // Double-check targetLanguage filter (in case index is missing and query didn't filter)
          if (data['targetLanguage'] === targetLanguage) {
            messages.push({
              id: docSnap.id,
              broadcastCode: data['broadcastCode'],
              sourceText: data['sourceText'],
              translatedText: data['translatedText'],
              sourceLanguage: data['sourceLanguage'],
              targetLanguage: data['targetLanguage'],
              timestamp: data['timestamp']?.toDate() || new Date(),
              speakerId: data['speakerId'],
              speakerEmail: data['speakerEmail'],
            });
          } else {
            console.warn(
              `[BroadcastTranslation] ⚠️ Message filtered out - expected ${targetLanguage}, got ${data['targetLanguage']}`
            );
          }
        });

        // Sort by timestamp - latest first (descending order)
        messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        this.messages$.next(messages);
        console.log(
          `[BroadcastTranslation] ✅ Received ${messages.length} messages for language: ${targetLanguage}`
        );
        if (messages.length > 0) {
          console.log(
            `[BroadcastTranslation] Sample message languages:`,
            messages.map((m) => m.targetLanguage).slice(0, 5)
          );
        }
      },
      (error) => {
        console.error(
          '[BroadcastTranslation] Error listening to messages:',
          error
        );
        // If composite index error, fall back to filtering in code
        if (error.code === 'failed-precondition') {
          console.warn(
            '[BroadcastTranslation] Composite index missing, using fallback query'
          );
          this.startListeningFallback(code, targetLanguage);
        } else {
          this.isListening$.next(false);
        }
      }
    );
  }

  /**
   * Load all existing messages for a broadcast code and target language
   */
  private async loadExistingMessages(
    code: string,
    targetLanguage: string
  ): Promise<void> {
    try {
      const messagesRef = collection(db, this.MESSAGES_COLLECTION);
      const q = query(
        messagesRef,
        where('broadcastCode', '==', code),
        where('targetLanguage', '==', targetLanguage),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      const messages: BroadcastMessage[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as any;
        // Double-check targetLanguage filter (in case index is missing and query didn't filter)
        if (data['targetLanguage'] === targetLanguage) {
          messages.push({
            id: docSnap.id,
            broadcastCode: data['broadcastCode'],
            sourceText: data['sourceText'],
            translatedText: data['translatedText'],
            sourceLanguage: data['sourceLanguage'],
            targetLanguage: data['targetLanguage'],
            timestamp: data['timestamp']?.toDate() || new Date(),
            speakerId: data['speakerId'],
            speakerEmail: data['speakerEmail'],
          });
        }
      });

      // Sort by timestamp - latest first (descending order)
      messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      this.messages$.next(messages);
      console.log(
        '[BroadcastTranslation] Loaded existing messages:',
        messages.length
      );
    } catch (error: any) {
      console.error(
        '[BroadcastTranslation] Error loading existing messages:',
        error
      );
      // If composite index error, use fallback
      if (error.code === 'failed-precondition') {
        await this.loadExistingMessagesFallback(code, targetLanguage);
      }
    }
  }

  /**
   * Fallback method if composite index is not available
   * Queries by broadcastCode only (no orderBy to avoid index requirement) and filters targetLanguage in code
   */
  private async loadExistingMessagesFallback(
    code: string,
    targetLanguage: string
  ): Promise<void> {
    try {
      const messagesRef = collection(db, this.MESSAGES_COLLECTION);
      // Query only by broadcastCode (no orderBy to avoid index requirement)
      const q = query(messagesRef, where('broadcastCode', '==', code));

      const snapshot = await getDocs(q);
      const messages: BroadcastMessage[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as any;
        // Filter by targetLanguage in code
        if (data['targetLanguage'] === targetLanguage) {
          messages.push({
            id: docSnap.id,
            broadcastCode: data['broadcastCode'],
            sourceText: data['sourceText'],
            translatedText: data['translatedText'],
            sourceLanguage: data['sourceLanguage'],
            targetLanguage: data['targetLanguage'],
            timestamp: data['timestamp']?.toDate() || new Date(),
            speakerId: data['speakerId'],
            speakerEmail: data['speakerEmail'],
          });
        }
      });

      // Sort by timestamp - latest first (descending order) - client-side
      messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      this.messages$.next(messages);
      console.log(
        '[BroadcastTranslation] Loaded existing messages (fallback):',
        messages.length
      );
    } catch (error) {
      console.error(
        '[BroadcastTranslation] Error loading existing messages (fallback):',
        error
      );
      // If even the simple query fails, set empty messages
      this.messages$.next([]);
    }
  }

  /**
   * Fallback listener if composite index is not available
   * Queries by broadcastCode only (no orderBy to avoid index requirement) and filters targetLanguage in code
   */
  private startListeningFallback(code: string, targetLanguage: string): void {
    const messagesRef = collection(db, this.MESSAGES_COLLECTION);
    // Query only by broadcastCode (no orderBy to avoid index requirement)
    const q = query(messagesRef, where('broadcastCode', '==', code));

    this.unsubscribeListener = onSnapshot(
      q,
      (snapshot) => {
        const messages: BroadcastMessage[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as any;
          // Filter by targetLanguage in code
          if (data['targetLanguage'] === targetLanguage) {
            messages.push({
              id: docSnap.id,
              broadcastCode: data['broadcastCode'],
              sourceText: data['sourceText'],
              translatedText: data['translatedText'],
              sourceLanguage: data['sourceLanguage'],
              targetLanguage: data['targetLanguage'],
              timestamp: data['timestamp']?.toDate() || new Date(),
              speakerId: data['speakerId'],
              speakerEmail: data['speakerEmail'],
            });
          }
        });

        // Sort by timestamp - latest first (descending order) - client-side
        messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        this.messages$.next(messages);
        console.log(
          `[BroadcastTranslation] ✅ Received ${messages.length} messages (fallback) for language: ${targetLanguage}`
        );
        if (messages.length > 0) {
          console.log(
            `[BroadcastTranslation] Sample message languages:`,
            messages.map((m) => m.targetLanguage).slice(0, 5)
          );
        }
      },
      (error) => {
        console.error(
          '[BroadcastTranslation] Error listening to messages (fallback):',
          error
        );
        this.isListening$.next(false);
      }
    );
  }

  /**
   * Stop listening to broadcast messages
   */
  stopListening(): void {
    if (this.unsubscribeListener) {
      this.unsubscribeListener();
      this.unsubscribeListener = undefined;
    }
    this.isListening$.next(false);
    this.messages$.next([]);
  }

  /**
   * Send a broadcast message (speaker side - called by backend)
   * This is called by the backend after transcription/translation
   */
  async sendBroadcastMessage(
    code: string,
    sourceText: string,
    translatedText: string,
    sourceLanguage: string,
    targetLanguage: string
  ): Promise<void> {
    try {
      const user = await this.authService.getCurrentUser();
      if (!user || !user.email) {
        throw new Error('User not authenticated');
      }

      const messageRef = doc(collection(db, this.MESSAGES_COLLECTION));
      const messageData = {
        broadcastCode: code,
        sourceText,
        translatedText,
        sourceLanguage,
        targetLanguage,
        timestamp: serverTimestamp(),
        speakerId: user.userId || user.email,
        speakerEmail: user.email,
      };

      console.log(`[BroadcastTranslation] 💾 Saving to Firestore:`, {
        broadcastCode: code,
        targetLanguage,
        sourceTextLength: sourceText.length,
        translatedTextLength: translatedText.length,
        translatedTextPreview: translatedText.substring(0, 50),
      });

      await setDoc(messageRef, messageData);

      console.log(
        `[BroadcastTranslation] ✅ Message saved to Firestore for broadcast ${code}, language: ${targetLanguage}`
      );
    } catch (error) {
      console.error('[BroadcastTranslation] Error sending message:', error);
      throw error;
    }
  }

  /**
   * End broadcast session and delete all messages and session
   */
  /**
   * End a broadcast session and delete all related data (session + messages)
   */
  async endBroadcastSession(code: string): Promise<void> {
    try {
      console.log(
        '[BroadcastTranslation] 🗑️ Ending broadcast session and cleaning up:',
        code
      );

      // Stop listening first to prevent new messages
      this.stopListening();

      // Delete all messages for this broadcast code
      console.log(
        '[BroadcastTranslation] 🗑️ Deleting all messages for broadcast:',
        code
      );
      await this.deleteBroadcastMessages(code);

      // Delete the broadcast session document
      console.log(
        '[BroadcastTranslation] 🗑️ Deleting broadcast session document:',
        code
      );
      const sessionRef = doc(db, this.BROADCAST_COLLECTION, code);
      await deleteDoc(sessionRef);

      // Clear local state
      this.currentSession$.next(null);
      this.messages$.next([]);

      console.log(
        '[BroadcastTranslation] ✅ Successfully ended and deleted broadcast session:',
        code
      );
    } catch (error) {
      console.error('[BroadcastTranslation] ❌ Error ending session:', error);
      // Don't throw - we want to clear local state even if Firestore deletion fails
      // Clear local state anyway
      this.currentSession$.next(null);
      this.messages$.next([]);
      this.stopListening();
    }
  }

  /**
   * Delete all messages for a broadcast code
   */
  private async deleteBroadcastMessages(code: string): Promise<void> {
    try {
      console.log(
        '[BroadcastTranslation] 🔍 Querying messages for broadcast:',
        code
      );
      const messagesRef = collection(db, this.MESSAGES_COLLECTION);
      const q = query(messagesRef, where('broadcastCode', '==', code));

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(
          '[BroadcastTranslation] ✅ No messages to delete for broadcast:',
          code
        );
        return;
      }

      console.log(
        `[BroadcastTranslation] 🗑️ Found ${snapshot.size} messages to delete`
      );

      // Use batch delete for efficiency (Firestore allows up to 500 operations per batch)
      const batch = writeBatch(db);
      let batchCount = 0;
      const maxBatchSize = 500;
      let totalDeleted = 0;

      snapshot.forEach((docSnap) => {
        if (batchCount < maxBatchSize) {
          batch.delete(docSnap.ref);
          batchCount++;
          totalDeleted++;
        }
      });

      // Commit the batch
      if (batchCount > 0) {
        await batch.commit();
        console.log(
          `[BroadcastTranslation] ✅ Deleted ${batchCount} messages for broadcast:`,
          code
        );
      }

      // If there are more than 500 messages, delete in batches
      if (snapshot.size > maxBatchSize) {
        console.log(
          `[BroadcastTranslation] 🔄 More than ${maxBatchSize} messages found, deleting remaining...`
        );
        // Recursively delete remaining messages
        await this.deleteBroadcastMessages(code);
      } else {
        console.log(
          `[BroadcastTranslation] ✅ Successfully deleted all ${totalDeleted} messages for broadcast:`,
          code
        );
      }
    } catch (error) {
      console.error(
        '[BroadcastTranslation] ❌ Error deleting broadcast messages:',
        error
      );
      // Don't throw - we want to continue with session deletion even if message deletion fails
      console.log(
        '[BroadcastTranslation] ⚠️ Continuing with session deletion despite message deletion error'
      );
    }
  }

  /**
   * Clear current session and messages
   */
  clear(): void {
    this.stopListening();
    this.currentSession$.next(null);
    this.messages$.next([]);
  }
}
